import { useDictStore } from "@/stores/dictStore";

import { PricingStrategy, PriceResult } from "../base/pricing.strategy";
import {
    isKompasDlb,
    isSteelSystem,
} from "@/app/predicates/systems.predicates";
import { MountsToptrackSlidingDoorTypeCode } from "@/app/constants/mounts";
import { isKompasPrefabricated } from "@/app/predicates/mounts.predicates";
import { unwrapRef } from "@/utils/reactive";

export class MountHardwarePricingStrategy extends PricingStrategy {
    private dictStore = useDictStore();

    public getPrices(...args: unknown[]): PriceResult {
        const [
            installedSystemCode,
            hardwareTypeCode,
            hardwareStructureType,
            colorCode,
        ] = args as string[];

        const finishColorInfo = this.dictStore.findFinishColor(unwrapRef(colorCode));
        const markup = parseFloat(finishColorInfo?.markup ?? "0");
        const isSteel = isSteelSystem(unwrapRef(installedSystemCode));

        const multiplicator =
            isKompasDlb(unwrapRef(installedSystemCode)) &&
            isKompasPrefabricated(hardwareStructureType)
                ? 1.5
                : 1;

        const pricesFromDict =
            hardwareTypeCode === MountsToptrackSlidingDoorTypeCode
                ? this.getMountTrackPrice(hardwareStructureType)
                : this.getMountHardwarePrice(
                      hardwareTypeCode,
                      hardwareStructureType,
                  );

        if (!this.validatePricesFromDict(pricesFromDict)) {
            return this.getDefaultPrices();
        }

        if (isSteel) {
            return {
                price: this.addMarkup(
                    pricesFromDict.price! * multiplicator,
                    markup,
                ),
                priceB2C: this.addMarkup(
                    pricesFromDict.priceB2C! * multiplicator,
                    markup,
                ),
                priceB2B: this.addMarkup(
                    pricesFromDict.priceB2B! * multiplicator,
                    markup,
                ),
            };
        }

        return {
            price: pricesFromDict.price! * multiplicator,
            priceB2C: pricesFromDict.priceB2C! * multiplicator,
            priceB2B: pricesFromDict.priceB2B! * multiplicator,
        };
    }

    private getMountHardwarePrice(
        hardwareTypeCode: string,
        hardwareStructureType: string,
    ) {
        return this.dictStore.findMountHardwarePrice(
            hardwareTypeCode,
            hardwareStructureType,
        );
    }

    private getMountTrackPrice(hardwareStructureType: string) {
        const track = this.dictStore.findMullionsTrackPrice(
            hardwareStructureType,
        );

        return {
            ...track,
            price: track?.price || 0,
            priceB2B: track?.price || 0,
            priceB2C: track?.price || 0,
        };
    }

    private addMarkup = (qty: number, percent: number): number => {
        if (percent === 0) return qty;
        return qty + qty * (percent / 100);
    };
}
